import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "./api";

export type ComputerStatus =
  | "setting_up"
  | "waiting"
  | "ready"
  | "sleeping"
  | "waking"
  | "asleep"
  | "released"
  | "error";

export type SetupStep =
  | "sandbox"
  | "waiting"
  | "restoring"
  | "installing"
  | "configuring"
  | "starting";

export type AiStatus = "none" | "connecting" | "connected" | "needs_reconnect" | "failed";

export type AiProvider =
  | "chatgpt"
  | "anthropic"
  | "openai"
  | "openrouter"
  | "grok"
  | "claude"
  | "workers-ai"
  | "runware";

/**
 * Where a connect is: waking the computer, signing in to ChatGPT or Grok (until it's
 * approved), saving the credential on it, starting OpenClaw.
 */
export type ConnectStage = "waking" | "signing_in" | "saving" | "waiting" | "starting";

/** When OpenClaw was last asked about an account (slice 1c spec §5.1). */
type Read = { read_at: string };

/** Whether the account can answer, from that read; a rest or a lack of credit ends at `until`. */
type ConnectionHealth = Read & {
  state: "ok" | "resting" | "out_of_credit" | "signed_out" | "unavailable";
  until?: string;
  /** When a Claude setup token runs out (slice 1g spec §6); absent for other credentials. */
  expires_at?: string;
  /** A Workers AI plan limit a turn hit (slice 1h spec §7). */
  code?: "free_allocation" | "paid_plan";
};

/** The usage OpenClaw reported at that read (slice 1c spec §5.2): a plan, limit windows, a balance. */
export type ConnectionUsage = Read & {
  windows: Array<{ used_pct: number; label: string; resets_at?: string }>;
  plan?: string;
  summary?: string;
};

/** One account on the computer (slice 1c spec §7): what the owner sees of it, never a key. */
export type AiConnection = {
  id: string;
  provider: AiProvider;
  label: string;
  status: "connecting" | "connected" | "needs_reconnect" | "failed";
  error?: string;
  /** Absent until a health read has run. */
  health?: ConnectionHealth;
  usage?: ConnectionUsage;
  position: number;
  connected_at: string | null;
  /** When the account was added (optional: a response from before it was sent). */
  created_at?: string;
  /** A Workers AI account's Cloudflare account ID (slice 1h spec §3.1). */
  account_id?: string;
};

export type MeArea = {
  id: string;
  slug: string;
  label: string;
  /** A chat icon's name (`@crew/ui/components/chat-icon`). */
  icon: string;
  position: number;
  open_tasks: number;
  /** Just you (slice 6g §8.2): its lock in the sidebar. */
  private?: boolean;
};

export type Me = {
  user: {
    id: string;
    email: string;
    name: string;
    initials: string;
    tint: string;
    role: "owner" | "member";
    /** The login email is confirmed (#384): checkout waits on it. */
    email_verified: boolean;
  };
  workspace: {
    id: string;
    name: string;
    timezone: string;
    status: "pending" | "active" | "canceled";
  };
  agents: Array<{
    id: string;
    slug: string;
    name: string;
    glyph: string;
    tint: string;
    about: string;
    status: "idle" | "working";
    /** Slice 6c §7.1: a handoff of its is open. Missing on an older Worker. */
    waiting_on_you?: boolean;
  }>;
  /** The sidebar's Areas, not archived, in order (slice 6d spec §11). */
  areas: MeArea[];
  /** `people`: the crew, not removed (slice 6a §6.5's workspace line). */
  counts: { open_approvals_mine: number; stuck_tasks: number; people: number; routines: number };
  computer: {
    status: ComputerStatus;
    error?: string;
    /** Where setup is (slice 1b spec §3.1), or null when not setting up. */
    step: SetupStep | null;
    setup_started_at: string | null;
    /** Slice 6c §6: Crew can show its desktop (Boat, or a mock computer's). Missing on an older Worker. */
    desktop?: boolean;
    /** Slice 6c §5: who holds the computer (a user id), or null. Missing on an older Worker. */
    held_by?: string | null;
    /** S-455: Take over works on a phone; false for a pi computer's lean box, desktop only for now. Missing on an older Worker. */
    phone_control?: boolean;
    /** Slice 6l: where the agent's browser is; on Kernel, each person has their own. Missing on an older Worker. */
    browser?: "box" | "kernel";
    /** Slice 1m §7: the agent keeps daily notes in Crew (a pi computer). Missing on an older Worker. */
    daily_notes?: boolean;
    ai: {
      status: AiStatus;
      /** Slice 1j: a run after a working AI; asks are taken and queued. Missing on an older Worker. */
      updating?: boolean;
      provider: AiProvider | null;
      model: string | null;
      error?: string;
      /** Where a connect is, or where it stopped when it failed. */
      stage?: ConnectStage;
      /** A sign-in in progress (ChatGPT's or Grok's). */
      pending?: { url: string; code: string; expires_at: string };
      /** The last successful connect; absent if it never connected. */
      connected_at?: string;
      /** Slice 1c: the fallback models, in order. */
      fallbacks?: string[];
      /** Slice 2f: the image model and its fallbacks; null, and Crew can't look at photos. */
      image?: { model: string; fallbacks: string[] } | null;
      /** Slice 1c: every account, with what the owner sees of it (never a key). */
      connections?: AiConnection[];
      /** Providers this workspace can't add: ChatGPT and Grok on a pi computer (spike S-429). */
      providers_off?: AiProvider[];
      /** ADR-0020: a pi computer takes Anthropic keys and a Claude subscription together. */
      anthropic_both?: boolean;
    };
  } | null;
};

export const meKey = ["me"] as const;

/** `/api/me`, or null when signed out. */
export const meQuery = {
  queryKey: meKey,
  queryFn: async (): Promise<Me | null> => {
    try {
      return await api<Me>("GET", "/api/me");
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
};

export function useMe() {
  return useQuery(meQuery);
}
